import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { Discount, DiscountForm, DiscountSearchForm } from 'entities/discount'

type DiscountsResponse = PagingResponse & {
    results: Discount[]
}

export function useFetchDiscountsApi(searchForm?: Form<DiscountSearchForm>): IndexApiSet<DiscountsResponse> & { execute: () => void } {
    const apiPath = 'discounts/'
    const api = useIndexApi<DiscountsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchDiscountApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `discounts/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostDiscountApi(): ApiSet<BaseResponse> & { execute: (form: Form<DiscountForm>) => void } {
    const apiSet = usePostApi<BaseResponse, DiscountForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<DiscountForm>) => {
        const apiPath = `discounts/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutDiscountApi(): ApiSet<BaseResponse> & { execute: (form: Form<DiscountForm>) => void } {
    const apiSet = usePutApi<BaseResponse, DiscountForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<DiscountForm>) => {
        const apiPath = `discounts/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutDiscountsApi(): ApiSet<BaseResponse> & { execute: (form: Form<DiscountForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, DiscountForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<DiscountForm[]>) => {
        const apiPath = `discounts/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteDiscountApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `discounts/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

